/**
 * Module 6D Phase 6 — Super-admin console (`/admin/*`).
 *
 * The cockpit. Tabs:
 *   • Studios (list → detail with actions)
 *   • Security (Bucket-B credential stuffing)
 *
 * Every cross-studio read routes through services/admin_data.py (the
 * gateway exempt from the 6G cross-tenant scanner). Every CHANGE
 * action surfaces a reason field client-side AND ≥20 char server-side.
 * Impersonation uses the Phase-3 launch URL contract (new tab, hash
 * consumed once into sessionStorage) — token plumbing is NOT
 * reimplemented here.
 */
import { Routes, Route, NavLink, useNavigate, Navigate } from "react-router-dom";
import { useAdminAuth } from "@/lib/admin_auth";
import { ShieldCheck, LogOut, Building2, AlertTriangle, Sparkles } from "lucide-react";
import { StudiosList, StudioDetail, BucketBView } from "@/pages/AdminShellPages";
import { AIDiagnosticsView } from "@/pages/AdminAIDiagnostics";


export default function AdminShell() {
  const { admin, loading, logout } = useAdminAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center"
           data-testid="admin-shell-loading">
        <div className="text-sm text-zinc-500">Loading admin surface…</div>
      </div>
    );
  }

  if (!admin) {
    navigate("/admin/login", { replace: true });
    return null;
  }

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  // Stash admin email for the impersonate launch URL
  try { localStorage.setItem("ls.admin.email", admin.email || ""); } catch { /* ignore */ }

  const navItem = ({ isActive }) =>
    `block px-3 py-2 text-xs uppercase tracking-widest border-l-2 ${
      isActive ? "border-amber-400 text-amber-300 bg-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-300"
    }`;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="admin-shell">
      <div className="flex items-center justify-between px-8 py-4 border-b border-zinc-800">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-amber-400" />
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-amber-400/80">
              Lead Studio — Internal
            </div>
            <div className="text-base text-zinc-100">Super-admin console</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-xs text-zinc-300" data-testid="admin-shell-email">{admin.email}</div>
            <div className="text-[10px] text-zinc-500">session ends {admin.expires_at?.slice(0, 16)}</div>
          </div>
          <button onClick={handleLogout} data-testid="admin-logout-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-zinc-700 hover:bg-zinc-900 text-zinc-300">
            <LogOut className="w-3 h-3" /> Sign out
          </button>
        </div>
      </div>

      <div className="flex">
        <aside className="w-44 border-r border-zinc-900 py-4 min-h-[calc(100vh-65px)]">
          <NavLink to="/admin/studios" className={navItem} data-testid="admin-nav-studios">
            <span className="inline-flex items-center gap-2"><Building2 size={13} /> Studios</span>
          </NavLink>
          <NavLink to="/admin/security/bucket-b" className={navItem} data-testid="admin-nav-bucket-b">
            <span className="inline-flex items-center gap-2"><AlertTriangle size={13} /> Bucket-B</span>
          </NavLink>
          <NavLink to="/admin/ai-diagnostics" className={navItem} data-testid="admin-nav-ai-diagnostics">
            <span className="inline-flex items-center gap-2"><Sparkles size={13} /> AI diagnostics</span>
          </NavLink>
        </aside>
        <main className="flex-1 px-8 py-8 min-w-0">
          <Routes>
            <Route index element={<Navigate to="/admin/studios" replace />} />
            <Route path="studios" element={<StudiosList />} />
            <Route path="studios/:studioId" element={<StudioDetail />} />
            <Route path="security/bucket-b" element={<BucketBView />} />
            <Route path="ai-diagnostics" element={<AIDiagnosticsView />} />
            <Route path="*" element={<Navigate to="/admin/studios" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
